import { Share } from "@capacitor/share";
import { encodeParam } from "./ApiHelper";

/**
 * Resolves the base URL used to build a shareable work-order link.
 *
 * In a PWA/web context window.location.origin is an http(s) origin and gives a
 * link that opens the app for the recipient. In a native context the origin is
 * capacitor://localhost (not shareable); until universal/app links are
 * configured, the link falls back to a relative path. Centralized here so the
 * base can be swapped for a configured public URL later.
 */
const getShareBaseUrl = (): string => {
  if (typeof window !== "undefined") {
    const origin = window.location?.origin;
    if (origin && /^https?:/i.test(origin)) {
      return origin.replace(/\/+$/, "");
    }
  }
  return "";
};

/** Builds the link to a specific work order. */
export const buildWorkOrderUrl = (woNumber: string): string => {
  const path = `/workorder/${encodeParam(woNumber)}`;
  const base = getShareBaseUrl();
  return base ? `${base}${path}` : path;
};

export interface ShareWorkOrderInput {
  woNumber: string;
  description?: string;
  comment?: string;
  labels: {
    title: string;
    dialogTitle: string;
    workOrderWord: string;
  };
}

/**
 * Builds the shareable message body: comment (if any) + WO line + link.
 * Shared by both the native/Web share and the copy-to-clipboard actions so
 * every channel gets identical content.
 */
export const buildWorkOrderShareText = (input: ShareWorkOrderInput): string => {
  const url = buildWorkOrderUrl(input.woNumber);
  const lines: string[] = [];
  const comment = input.comment?.trim();
  if (comment) lines.push(comment);

  let woLine = `${input.labels.workOrderWord} ${input.woNumber}`;
  if (input.description) woLine += ` - ${input.description}`;
  lines.push(woLine);
  lines.push(url);

  return lines.join("\n\n");
};

/**
 * Copies the work-order share message to the clipboard. Uses the async
 * Clipboard API, falling back to a hidden textarea + execCommand for older
 * webviews. Must be called from a user gesture. Returns true on success.
 */
export const copyWorkOrderToClipboard = async (
  input: ShareWorkOrderInput
): Promise<boolean> => {
  const text = buildWorkOrderShareText(input);
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // fall through to legacy path
  }
  try {
    const textarea = document.createElement("textarea");
    textarea.value = text;
    textarea.style.position = "fixed";
    textarea.style.opacity = "0";
    document.body.appendChild(textarea);
    textarea.focus();
    textarea.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(textarea);
    return ok;
  } catch {
    return false;
  }
};

/**
 * Shares a work-order link (plus an optional comment) via the native share
 * sheet / Web Share API, falling back to a mail draft when neither is
 * available (e.g. desktop browsers without navigator.share). Must be called
 * from a user gesture. Returns true if a share/fallback was launched.
 */
export const shareWorkOrder = async (
  input: ShareWorkOrderInput
): Promise<boolean> => {
  const url = buildWorkOrderUrl(input.woNumber);
  const text = buildWorkOrderShareText(input);
  const title = input.labels.title;

  const canShare = await Share.canShare().catch(() => ({ value: false }));
  if (canShare?.value) {
    try {
      await Share.share({
        title,
        text,
        url,
        dialogTitle: input.labels.dialogTitle,
      });
    } catch {
      // User cancelled the share sheet — do NOT fall back to mail.
    }
    return true;
  }

  // Share not available (e.g. desktop browser) — open a pre-filled mail draft.
  const subject = encodeURIComponent(title);
  const body = encodeURIComponent(text);
  window.open(`mailto:?subject=${subject}&body=${body}`, "_blank");
  return true;
};
